import { useMemo, useRef } from "react";
import { CHART_COLORS } from "../../../ui/utils";
import type { FailureMode } from "../types";
import { assignVisibleSlots } from "../utils/modeColors";
import { UNASSIGNED_COLOR } from "../utils/palette";

export interface ModeColors {
  /** Palette colour for a mode id; grey for the `null` unassigned pool. */
  colorForMode: (id: string | null) => string;
}

/**
 * A session-stable `mode id → colour` mapping shared by the scatter, legend, and
 * detail panel (design doc 015 §6). See `assignVisibleSlots` for the scheme: the
 * modes drawn on the map get distinct palette colours, sticky for the lifetime of
 * the page (they hold across panning, the daily/weekly toggle, and slider
 * scrubbing), and reset only on a hard refresh — which is all the stability the
 * feature needs.
 */
export function useModeColors(
  modes: FailureMode[],
  visibleModeIds: ReadonlySet<string>,
): ModeColors {
  // Sticky across renders; a fresh page mount (refresh) starts empty.
  const slotByMode = useRef<Map<string, number>>(new Map());

  const colorForMode = useMemo(() => {
    const orderedIds = modes.map((m) => m.failure_mode_id);
    assignVisibleSlots(
      orderedIds,
      visibleModeIds,
      slotByMode.current,
      CHART_COLORS.length,
    );
    // Deterministic fallback (registry position) for a mode that has never been
    // drawn, so its legend swatch is stable until it's first shown and gets a
    // real, coordinated slot.
    const registryIndex = new Map(orderedIds.map((id, i) => [id, i]));
    return (id: string | null): string => {
      if (id === null) return UNASSIGNED_COLOR;
      const slot = slotByMode.current.get(id);
      if (slot !== undefined) return CHART_COLORS[slot];
      const idx = registryIndex.get(id) ?? 0;
      return CHART_COLORS[idx % CHART_COLORS.length];
    };
  }, [modes, visibleModeIds]);

  return { colorForMode };
}
